import {
  Col,
  Row,
  Table,
  TableColumnsType,
  Typography,
  notification,
  Tooltip,
  TablePaginationConfig,
} from "antd";
import { useNavigate } from "react-router-dom";
import { DeleteOutlined } from "@ant-design/icons";
import moment from "moment/moment";
import React, { useEffect, useState } from "react";
import { ApplicationAction } from "../../model_gen/application";
import { ApplicationService } from "../../services/ApplicationService";
import { usePermissions } from "../../PermissionsProvider";
import editIcon from "../../assets/svg/edit-icon.svg";
import TableDropDownMenu from "../shared/tablemenus/TableDropdownMenu";
import { ApplicationActionsListProps } from "../../types/IApplication";
import { InfoCircleOutlined } from "@ant-design/icons";

const { Title } = Typography;

export const ApplicationActionList: React.FC<ApplicationActionsListProps> = (
  props,
) => {
  const { apiKey } = usePermissions();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [applicationActions, setApplicationActions] = useState<ApplicationAction[]>([]);

  const deleteApplicationAction = (actionId: number) => {
    if (!props.applicationId) return;
    if (
      window.confirm("Are you sure you want to delete this application action?")
    ) {
      ApplicationService.deleteApplicationAction(
        props.applicationId,
        actionId,
        setIsLoading,
        apiKey,
      ).subscribe(
        () => {
          getApplicationActions();
          notification.success({
            message: "Success",
            description: "Application action deleted successfully",
          });
        },
        (error: any) => {
          console.error(error);
          notification.error({
            message: "Error",
            description: "Failed to delete application action",
          });
        },
      );
    }
  };
  const getApplicationActions = () => {
    if (!props.applicationId) return;
    ApplicationService.getApplicationActions(
      props.applicationId,
      setIsLoading,
      apiKey,
    ).subscribe(
      (actions: ApplicationAction[]) => {
        // Sort actions by last_activity in descending order
        const sortedActions = actions.sort((a, b) => {
          const dateA = a.last_activity > 0 ? a.last_activity : 0;
          const dateB = b.last_activity > 0 ? b.last_activity : 0;
          return dateB - dateA;
        });
        setApplicationActions(sortedActions);
      },
      (error: any) => {
        console.error(error);
      },
    );
  };

  useEffect(() => {
    getApplicationActions();
  }, [props.applicationId, apiKey]);


  const actionColumns: TableColumnsType<ApplicationAction> = [
    {
      title: "Name",
      dataIndex: "action_type",
      key: "session_id",
      sorter: (a, b) => a.action_type.localeCompare(b.action_type),
    },
    {
      title: "Date Created",
      dataIndex: "date_created",
      key: "date_created",
      render: (date: string) => moment(date).format("YYYY-MM-DD hh:mm:ss A"),
      sorter: (a, b) => a.action_type.localeCompare(b.action_type),
    },
    {
      title: "Last Activity",
      dataIndex: "last_activity",
      key: "last_activity",
      render: (date) => {
        if (date > 0) {
          return moment(date).format("YYYY-MM-DD hh:mm:ss A");
        } else {
          return "N/A";
        }
      },
      sorter: (a, b) =>
        moment(a.last_activity).unix() - moment(b.last_activity).unix(),
    },
    {
      title: "Requests",
      dataIndex: "request_count",
      key: "request_count",
      render: (count: number) => count.toString(),
      sorter: (a, b) => a.request_count - b.request_count,
    },
    {
      key: "intent",
      render: (_, action) => {
        const items = [
          {
            label: (
              <a
                rel="noopener noreferrer"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(
                    `/application/${action.application_id}/intent/${action.id}`,
                  );
                }}>
                Edit
              </a>
            ),
            key: "0",
            icon: <img src={editIcon} width={15} />,
          },
          {
            label: (
              <a
                rel="noopener noreferrer"
                onClick={(e) => {
                  e.stopPropagation();
                  deleteApplicationAction(action.id);
                }}>
                Delete
              </a>
            ),
            key: "1",
            icon: <DeleteOutlined />,
          },
        ];
        return <TableDropDownMenu items={items} />;
      },
      align: "right",
    },
  ];

  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 5;

  const handleTableChange = (pagination: TablePaginationConfig) => {
    setCurrentPage(pagination.current || 1);
  };

  return (
    <div>
      <Row align="middle">
        <Col span={12}>
          <div style={{ display: "flex", alignItems: "center", marginTop: 20 }}>
            <Title level={4}>Intents</Title>
            <Tooltip title="Intents are used to differentiate different request types within the same application">
              <InfoCircleOutlined
                style={{ marginLeft: 4, color: "rgba(0, 0, 0, 0.45)" }}
              />
            </Tooltip>
          </div>
        </Col>
      </Row>
      <Table
        rowKey={"id"}
        columns={actionColumns}
        dataSource={applicationActions}
        pagination={
          applicationActions.length > 5
            ? {
                current: currentPage,
                pageSize: pageSize,
                total: applicationActions.length,
                showSizeChanger: false,
              }
            : false
        }
        loading={isLoading}
        onChange={handleTableChange}
        onRow={(record) => {
          return {
            onClick: (e) => {
              const targetId =
                ((e.target as Element).parentElement as Element).id ?? "";
              const isDropdownClick =
                targetId === "table-dropdown-space" ||
                targetId === "table-dropdown";

              if (!isDropdownClick)
                navigate(
                  `/application/${record.application_id}/intent/${record.id}`,
                );
            },
          };
        }}
      />
    </div>
  );
};
